import { portalConfig } from './portal-config.js';

export class HttpError extends Error {
  constructor(public readonly status: number, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = 'HttpError';
  }
}

/** The id token no longer authorizes; the caller should refresh the session, not sign out. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

// A token about to expire is treated the same as one that just has - the authorizer rejects both.
const CLOCK_SKEW_MS = 30_000;

function decodeJwtExpMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const { exp } = JSON.parse(atob(b64)) as { exp?: unknown };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, nowMs = Date.now()): boolean {
  const expMs = decodeJwtExpMs(token);
  return expMs !== null && nowMs >= expMs - CLOCK_SKEW_MS;
}

export async function apiGet<T>(path: string, idToken: string): Promise<T> {
  const res = await fetch(`${portalConfig.apiBase}${path}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  // A missing Authorization header is a 401 from API Gateway itself, but the common case - a present
  // but expired id token - is rejected by the SIMPLE Lambda authorizer, which surfaces as 403. That
  // 403 is an expiry signal too, so both reach the same refresh path. A 403 with a still-valid token
  // is a genuine denial (wrong hosted domain) and must surface as one - refreshing it would loop.
  if (res.status === 401 || (res.status === 403 && isTokenExpired(idToken))) {
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    throw new HttpError(res.status);
  }
  return res.json() as Promise<T>;
}
